// SideChatPanel.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createChartConversation } from '../services/chatService';
import searchMic from '../assets/icons/search-mic.svg';
import searchMicActive from '../assets/icons/search-mic-active.svg';
import { setStopId } from '../redux/counterSlice';
import { v4 as uuidv4 } from "uuid";
import { mode } from 'crypto-js';
import VoiceSearch from './VoiceSearch';
import { Dispatch } from '@reduxjs/toolkit';
import { stat } from 'fs';
import { getAnswer } from '../services/chatBotService';


interface Message {
  id: string;
  type: 'user' | 'bot' | 'thinking';
  content: string;
  audioUrl?: string;
}

interface SideChatPanelProps {
  title?: string;
  context?: string;
  onClose?: () => void;
  onRestart?: () => void;
  // For maps
  customdata?: any;
  location?: string | number;
  // For graphs
  x?: any;
  y?: any;
  // For pie charts
  label?: string | number;
  value?: number;
  // For history
  initialHistory?: any[];
  // Chart conversation metadata
  chart_conversation_id?: string;
  chart_type?: string;
  chart_title?: string;
  x_axis?: string;
  y_axis?: string;
  plotJson?: any;
}

const SideChatPanel: React.FC<SideChatPanelProps> = ({
  title = "Chart Context",
  context = "",
  onClose,
  onRestart,
  customdata,
  location,
  x,
  y,
  label,
  value,
  initialHistory = [],
  chart_conversation_id,
  chart_type,
  chart_title,
  x_axis,
  y_axis,
  plotJson
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isAuthDetails = useSelector((state: any) => state.persistent.isAuth);
  const ishandleModeDetails = useSelector((state: any) => state.persistent.handleMode);
  const [getAnswerValue, setAnswerValue] = useState<boolean>(false);

  // ElevenLabs API configuration
  const ELEVEN_API_KEY = process.env.REACT_APP_ELEVEN_API_KEY || "sk_cfc28fa09746876c70d5070f5b7c8fdee25deab3b54973d6";
  const ELEVEN_MODEL = process.env.REACT_APP_ELEVEN_MODEL || "eleven_multilingual_v2";
  const VOICE_ID = process.env.REACT_APP_ELEVEN_VOICE_ID || "19STyYD15bswVz51nqLf";

  // Generate audio for bot response
  const generateAudio = useCallback(async (text: string): Promise<string | null> => {
    if (!text || typeof text !== "string") {
      return null;
    }

    try {
      const response = await fetch(
        `https://api.elevenlabs.io/v1/text-to-speech/${VOICE_ID}`,
        {
          method: 'POST',
          headers: {
            'Accept': 'audio/mpeg',
            'Content-Type': 'application/json',
            'xi-api-key': ELEVEN_API_KEY
          },
          body: JSON.stringify({
            text: text,
            model_id: ELEVEN_MODEL,
            voice_settings: {
              stability: 0.5,
              similarity_boost: 0.75
            }
          })
        }
      );

      if (!response.ok) {
        throw new Error(`ElevenLabs API error: ${response.statusText}`);
      }

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      return audioUrl;
    } catch (error) {
      console.error('Error generating audio with ElevenLabs:', error);
      return null;
    }
  }, [ELEVEN_API_KEY, ELEVEN_MODEL, VOICE_ID]);
  // Convert history items to message format
  const convertHistoryToMessages = useCallback((history: any[]): Message[] => {
    if (!history || history.length === 0) return [];

    return history.map((item: any) => {
      const content =
        item.message ||
        item.text ||
        item.content ||
        item.question ||
        JSON.stringify(item);

      const type = item.type || (item.question ? 'user' : 'bot');

      return {
        id: uuidv4(),
        type,
        content
      };
    });
  }, []);


  // Initialize messages with history
  useEffect(() => {
    setMessages(convertHistoryToMessages(initialHistory));
  }, [initialHistory, convertHistoryToMessages]);

  // Extract metric data from props
  const extractMetricData = useCallback(() => {
    if (value !== undefined) {
      return {
        metric: label?.toString() || 'value',
        metricValue: value
      };
    } else if (y !== undefined) {
      return {
        metric: y?.toString() || 'y',
        metricValue: y
      };
    } else if (location !== undefined) {
      return {
        metric: 'location',
        metricValue: location
      };
    }
    return { metric: undefined, metricValue: undefined };
  }, [value, label, y, location]);

  // Prepare request body
  const prepareRequestBody = useCallback((question: string) => {
    const userId = localStorage.getItem("user_id");
    const sessionId = localStorage.getItem("session_id");
    const conversationId = localStorage.getItem("activeSessionId");
    const { metric, metricValue } = extractMetricData();

    return {
      user_id: userId,
      session_id: sessionId,
      question: question,
      conversation_id: conversationId,
      chart_conversation_id: chart_conversation_id,
      chart_type: chart_type || 'bar',
      chart_title: chart_title || '',
      x_axis: x?.toString() || '',
      y_axis: y?.toString() || '',
      location: location,
      metric: metric,
      metric_value: metricValue?.toString(),
      group: (isAuthDetails?.group_name || '').toLowerCase(),
      title: title || 'Chart Conversation'
    };
  }, [chart_conversation_id, chart_type, chart_title, x, y, location, extractMetricData, isAuthDetails, title]);

  // Remove thinking message from messages
  const removeThinkingMessage = useCallback(() => {
    setMessages(prev => prev.filter(msg => msg.type !== 'thinking'));
  }, []);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;
  
    const question = input.trim();
    const botMessageId = uuidv4(); // Generate ID early to reference later
    setInput("");
  
    // 1. 👤 Add User message
    setMessages(prev => [
      ...prev,
      { id: uuidv4(), type: 'user', content: question }
    ]);
  
    // 2. 🤔 Add Thinking loader
    const thinkingId = uuidv4();
    setMessages(prev => [
      ...prev,
      { id: thinkingId, type: 'thinking', content: 'Thinking...' }
    ]);
  
    setIsLoading(true);
  
    try {
      const requestBody = prepareRequestBody(question);
      const { result, error } = await createChartConversation(requestBody);
  
      // ❌ Remove thinking immediately
      setMessages(prev => prev.filter(msg => msg.id !== thinkingId));
      setAnswerValue(true);
  
      if (error) throw new Error(error);
  
      const botResponse =
        result?.response ||
        result?.message ||
        result?.answer ||
        result?.content ||
        "No response received";
  
      // 3. ✅ IMMEDIATELY show the text response
      setMessages(prev => [
        ...prev,
        { 
          id: botMessageId, 
          type: 'bot', 
          content: botResponse 
        }
      ]);
  
      // 4. 🔊 Fetch audio in the background (don't "await" before showing text)
      generateAudio(botResponse).then((audioUrl) => {
        if (audioUrl) {
          setMessages(prev => 
            prev.map(msg => 
              msg.id === botMessageId 
                ? { ...msg, audioUrl } 
                : msg
            )
          );
        }
      });
  
    } catch (err: any) {
      setMessages(prev => prev.filter(msg => msg.id !== thinkingId));
      const errorMessage = `Error: ${err.message}`;
      
      setMessages(prev => [
        ...prev,
        { id: uuidv4(), type: 'bot', content: errorMessage }
      ]);
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <>
      <style>{`
        #side-chat-panel .jumping-dots span {
          position: relative;
          -webkit-animation: jump 1500ms infinite;
          animation: jump 2s infinite;
          display: inline-block;
          background-color: #9ca3af;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          margin-right: 4px;
        }
        #side-chat-panel .jumping-dots .dot-1 {
          animation-delay: 200ms;
          -webkit-animation-delay: 200ms;
        }
        #side-chat-panel .jumping-dots .dot-2 {
          animation-delay: 400ms;
          -webkit-animation-delay: 400ms;
        }
        #side-chat-panel .jumping-dots .dot-3 {
          animation-delay: 600ms;
          -webkit-animation-delay: 600ms;
        }
        @keyframes jump {
          0% {
            bottom: 0px;
          }
          20% {
            bottom: 6px;
          }
          40% {
            bottom: 0px;
          }
        }
      `}</style>
      <div
        id="side-chat-panel"
        style={{
          position: "fixed",
          bottom: 20,
          right: 20,
          width: 380,
          height: 520,
          background: "rgba(255,255,255,0.95)",
          backdropFilter: "blur(10px)",
          borderRadius: 16,
          border: "1px solid #e5e7eb",
          boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
          transform: "translateY(0%)",
          opacity: 1,
          transition: "all 0.4s cubic-bezier(0.18, 0.89, 0.32, 1.28)",
          zIndex: 999,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <ChatHeader
          title={title}
          context={context}
          onClose={onClose}
          onRestart={onRestart}
          customdata={customdata}
          location={location}
          x={x}
          y={y}
          label={label}
          value={value}
        />

        {/* Messages */}
        <ChatMessages messages={messages} mode2={ishandleModeDetails} isAnswer={getAnswerValue} />

        {/* Input */}
        <ChatInput
          input={input}
          setInput={setInput}
          onSend={handleSend}
          isLoading={isLoading}
          mode={ishandleModeDetails}
        />
      </div>
    </>
  );
};

export default SideChatPanel;

// ChatHeader.tsx
interface ChatHeaderProps {
  title: string;
  context: string;
  onClose?: () => void;
  onRestart?: () => void;
  customdata?: any;
  location?: string | number;
  x?: any;
  y?: any;
  label?: string | number;
  value?: number;
}

export const ChatHeader: React.FC<ChatHeaderProps> = ({
  title,
  context,
  onClose,
  onRestart,
  customdata,
  location,
  x,
  y,
  label,
  value
}) => {
  // Build detailed context based on passed data
  const buildDetailedContext = () => {
    if (customdata !== undefined || location !== undefined) {
      // Map data
      const parts: string[] = [];
      if (location !== undefined) parts.push(`Location: ${location}`);
      if (customdata !== undefined) {
        try {
          const customStr = typeof customdata === 'string' ? customdata : JSON.stringify(customdata);
          if (customStr.length > 50) {
            parts.push(`Custom Data: ${customStr.substring(0, 50)}...`);
          } else {
            parts.push(`Custom Data: ${customStr}`);
          }
        } catch {
          parts.push('Custom Data: [Object]');
        }
      }
      return parts.length > 0 ? parts.join(' | ') : context;
    } else if (label !== undefined || value !== undefined) {
      // Pie chart data
      const parts: string[] = [];
      if (label !== undefined) parts.push(`Label: ${label}`);
      if (value !== undefined) parts.push(`Value: ${value}`);
      return parts.length > 0 ? parts.join(' | ') : context;
    } else if (x !== undefined || y !== undefined) {
      // Graph data
      const parts: string[] = [];
      if (x !== undefined) parts.push(`${x}`);
      if (y !== undefined) parts.push(`${y}`);
      return parts.length > 0 ? parts.join(' | ') : context;
    }
    return context;
  };

  return (
    <div
      style={{
        padding: "14px 18px",
        borderBottom: "1px solid #e5e7eb",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        background: "linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 15, color: "#111827", marginBottom: 4 }}>
          {title}
        </div>
        <div
          style={{
            fontSize: 12,
            color: "#6b7280",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }}
          title={buildDetailedContext()}
        >
          {buildDetailedContext()}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: 12 }}>
        {onRestart && (
          <button
            onClick={onRestart}
            style={{
              background: "none",
              border: "none",
              fontSize: 18,
              cursor: "pointer",
              color: "#6b7280",
              padding: "4px 8px",
              borderRadius: 6,
              transition: "all 0.2s",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#f3f4f6";
              e.currentTarget.style.color = "#111827";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "none";
              e.currentTarget.style.color = "#6b7280";
            }}
            title="Restart chat"
          >
            ↻
          </button>
        )}
        {onClose && (
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: 18,
              cursor: "pointer",
              color: "#6b7280",
              padding: "4px 8px",
              borderRadius: 6,
              transition: "all 0.2s",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#fee2e2";
              e.currentTarget.style.color = "#dc2626";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "none";
              e.currentTarget.style.color = "#6b7280";
            }}
            title="Close"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
};

// ChatMessages.tsx
interface ChatMessagesProps {
  messages: Message[];
  mode2: string
  isAnswer: boolean
}

export const ChatMessages: React.FC<ChatMessagesProps> = ({ messages, mode2, isAnswer }) => {
  const dispatch = useDispatch();
  const stopId = useSelector((state: any) => state.counter.stopId);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);

  const handleSpeak = (audioUrl: string | undefined, id: string | number) => {
    if (!audioUrl) {
      console.warn('No audio URL available for this message');
      return;
    }

    // If the same message is already playing, stop it
    if (stopId === id && currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
      currentAudioRef.current = null;
      dispatch(setStopId(null));
      return;
    }

    // Stop any currently playing audio
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
      currentAudioRef.current = null;
      dispatch(setStopId(null));
    }

    try {
      // Create audio element and play pre-generated audio
      const audio = new Audio(audioUrl);
      currentAudioRef.current = audio;
      dispatch(setStopId(id));

      audio.onended = () => {
        currentAudioRef.current = null;
        dispatch(setStopId(null));
      };

      audio.onerror = (error) => {
        console.error('Audio playback error:', error);
        currentAudioRef.current = null;
        dispatch(setStopId(null));
      };

      audio.play();
    } catch (error) {
      console.error('Error playing audio:', error);
      dispatch(setStopId(null));
      currentAudioRef.current = null;
    }
  };

  // Cleanup: stop audio when component unmounts
  useEffect(() => {
    return () => {
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current.currentTime = 0;
        currentAudioRef.current = null;
      }
    };
  }, []);

  return (
    <div
      id="side-chat-messages"
      style={{
        flex: 1,
        overflowY: "auto",
        padding: "12px 16px"
      }}
    >
      {messages.length === 0 ? (
        <div style={{ fontSize: 13, color: "#9ca3af", textAlign: "center" }}>
          Ask anything to get more insights...
        </div>
      ) : (
        messages.map(msg => {
          if (msg.type === "thinking") {
            return (
              <div key={msg.id} style={{ marginBottom: 12 }}>
                <div className={`output-message-container visible opacity-100`}>
                  <p>
                    <span className="jumping-dots">
                      <span className="dot-1"></span>
                      <span className="dot-2"></span>
                      <span className="dot-3"></span>
                    </span>
                  </p>
                </div>
              </div>
            );
          }

          const isUser = msg.type === "user";

          return (
            <div
              key={msg.id}
              style={{
                display: "flex",
                justifyContent: isUser ? "flex-end" : "flex-start",
                marginBottom: 12
              }}
            >
              <div
                style={{
                  background: isUser ? "#e0e7ff" : "#f3f4f6",
                  padding: "10px 14px",
                  borderRadius: 8,
                  maxWidth: "80%",
                  position: "relative"
                }}
              >
                <div style={{ fontSize: 13, lineHeight: 1.5 }}>
                  {!isUser && (mode2 === 'hybrid' || mode2 === 'voice') && (
                    <div>
                      <button
                        onClick={() => handleSpeak(msg.audioUrl, msg.id)}
                        disabled={!msg.audioUrl}
                        style={{
                          marginTop: 6,
                          fontSize: 11,
                          border: "none",
                          background: "transparent",
                          color: msg.audioUrl ? "#2563eb" : "#9ca3af",
                          cursor: msg.audioUrl ? "pointer" : "not-allowed",
                          opacity: msg.audioUrl ? 1 : 0.6
                        }}
                      >
                        {stopId === msg.id ? "Stop 🔇" : msg.audioUrl ? "Speak 🔊" : "Preparing Audio..."}
                      </button>
                    </div>
                  )}
                  {msg.content}
                </div>
{/* 
                {!isUser && (mode2 === 'text') && (
                  <div style={{ fontSize: 13, lineHeight: 1.5 }}>
                    {isAnswer ? "" : "Thinking..."}
                  </div>
                )} */}

                
              </div>

            </div>
          );
        })
      )}
    </div>
  );
};

// ChatInput.tsx
interface ChatInputProps {
  input: string;
  setInput: React.Dispatch<React.SetStateAction<string>>
  onSend: () => void;
  isLoading?: boolean;
  mode?: string;
  isKeyDown?: boolean;
  setIsKeyDown?: (val: boolean) => void;
  setMessage?: (val: string) => void;
  inputRef?: React.RefObject<HTMLInputElement>;
  sendButtonRef?: React.RefObject<HTMLButtonElement>;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  input,
  setInput,
  onSend,
  isLoading = false,
  mode,
  setMessage,
  inputRef,
  sendButtonRef
}) => {
  const [isKeyDown, setIsKeyDown] = useState(false);
  const localInputRef = useRef<HTMLInputElement>(null);
  const localSendRef = useRef<HTMLButtonElement>(null);
  
  // Voice button click handler - use refs instead of querySelector for isolation

  const voiceSearch = VoiceSearch(
    setIsKeyDown,
    localInputRef.current,
    localSendRef.current,
    setInput,
    'sidechat-drilldown'
  );
  const handleStartListening = voiceSearch.handleStartListening;

  const handleVoiceIconClicked = () => {
    setIsKeyDown(true);
    setInput('Listening...');

    if (localInputRef?.current) {
      localInputRef.current.setAttribute('disabled', 'true');
      localInputRef.current.focus();
    }

    if (localSendRef?.current) {
      localSendRef.current.setAttribute('disabled', 'true');
    }

    handleStartListening();
    localInputRef?.current?.focus();
  };

  return (
    <div
      style={{
        padding: "12px 16px",
        borderTop: "1px solid #e5e7eb",
        display: "flex",
        gap: 10,
        background: "#ffffff",
      }}
    >
      <input
        ref={localInputRef}
        type="text"
        value={input}
        onChange={e => setInput(e.target.value)}
        placeholder="Ask about this data..."
        disabled={isLoading || mode === 'voice'} // disable typing in pure voice mode
        style={{
          flex: 1,
          padding: "10px 14px",
          border: "1px solid #d1d5db",
          borderRadius: 10,
          fontSize: 13,
          outline: "none",
          transition: "all 0.2s",
          opacity: isLoading ? 0.6 : 1,
          background: isLoading ? "#f9fafb" : "#ffffff",
        }}
        onFocus={(e) => {
          if (!isLoading) {
            e.currentTarget.style.borderColor = "#4f46e5";
            e.currentTarget.style.boxShadow = "0 0 0 3px rgba(79, 70, 229, 0.1)";
          }
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = "#d1d5db";
          e.currentTarget.style.boxShadow = "none";
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !isLoading && input.trim()) {
            onSend();
          }
        }}
      />

      {/* Voice button */}
      {(mode && (mode === 'voice' || mode === 'hybrid')) && (
        <button
          onClick={handleVoiceIconClicked}
          title="Search via Voice"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 6,
          }}
        >
          <img
            src={isKeyDown ? searchMicActive : searchMic} // show active/inactive mic
            alt="voice search"
            style={{ width: 20, height: 20 }}
          />
        </button>
      )}

      {/* Send button */}
      <button
        ref={localSendRef}
        onClick={onSend}
        disabled={isLoading || !input.trim()}
        style={{
          background: isLoading || !input.trim() ? "#d1d5db" : "#4f46e5",
          color: "#fff",
          border: "none",
          padding: "10px 20px",
          borderRadius: 10,
          cursor: isLoading || !input.trim() ? "not-allowed" : "pointer",
          fontSize: 13,
          fontWeight: 500,
          transition: "all 0.2s",
          minWidth: 70,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
        onMouseEnter={(e) => {
          if (!isLoading && input.trim()) {
            e.currentTarget.style.background = "#4338ca";
            e.currentTarget.style.transform = "translateY(-1px)";
            e.currentTarget.style.boxShadow = "0 4px 6px rgba(79, 70, 229, 0.2)";
          }
        }}
        onMouseLeave={(e) => {
          if (!isLoading && input.trim()) {
            e.currentTarget.style.background = "#4f46e5";
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "none";
          }
        }}
      >
        {isLoading ? "..." : "Send"}
      </button>
    </div>
  );
};

